"use client";

import { c as _c } from "react/compiler-runtime";
import { ColumnDef, Row } from "@tanstack/react-table";
import { FileText } from "lucide-react";
import { useProgressNoteColumnFilters } from "@/src/components/cards/progress-note-card/progress-note-columns";
import { ProgressNoteToolbar } from "@/src/components/cards/progress-note-card/progress-note-toolbar";
import { CopyableContent } from "@/src/components/copyable-content";
import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { DataTable } from "@/src/components/tables/data-table";
import { DataTableColumnHeader } from "@/src/components/tables/data-table-column-header";
import { EnabledFields } from "@/src/lib/schema-types";
import { getClassificationDisplay } from "@/src/lib/utils/classification";
import { formatDate } from "@/src/lib/utils/date-and-phone-utils";
import { isFieldEnabled, hasValue } from "@/src/lib/utils/object-utils";
import { Classification } from "@/src/models/constants/classifications";
import { ProgressNote } from "@/src/models/documents/progress-note/progress-note";
import { formatICD10CodeForDisplay } from "@/src/models/icd10";
interface ProgressNoteWithClassification extends ProgressNote {
  classification: Classification;
}
interface ProgressNoteTableProps {
  progressNotes: (ProgressNote & {
    classification: Classification;
  })[];
  enabledFields?: EnabledFields;
}
export function ProgressNoteTable(t0) {
  const $ = _c(12);
  const {
    progressNotes,
    enabledFields
  } = t0;
  const [columnFilters] = useProgressNoteColumnFilters();
  let t1;
  if ($[0] !== columnFilters.progressNoteClassification) {
    t1 = {
      classification: columnFilters.progressNoteClassification
    };
    $[0] = columnFilters.progressNoteClassification;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mappedColumnFilters = t1;
  const dataWithClassification = progressNotes;
  const CellWrapper = _temp;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = _temp2;
    t3 = t4 => {
      const {
        row
      } = t4;
      const value = row.getValue("classification") as Classification;
      const displayValue = getClassificationDisplay(value);
      return <CellWrapper><span className="no-select"><CopyableContent text={displayValue} /></span></CellWrapper>;
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== dataWithClassification || $[5] !== enabledFields) {
    const allColumns = [{
      accessorKey: "classification",
      header: t2,
      cell: t3,
      filterFn: "arrIncludesSome"
    }, {
      accessorKey: "dateOfProgressNote",
      header: _temp3,
      cell: t5 => {
        const {
          row: row_0
        } = t5;
        const value_0 = row_0.getValue("dateOfProgressNote") as string;
        const displayValue_0 = value_0 ? formatDate(value_0) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_0} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "progressNoteType",
      header: _temp4,
      cell: t6 => {
        const {
          row: row_1
        } = t6;
        const value_1 = row_1.getValue("progressNoteType") as string;
        return <CellWrapper><span className="no-select"><CopyableContent text={value_1 || "\u2014"} /></span></CellWrapper>;
      },
      filterFn: "arrIncludesSome"
    }, {
      accessorKey: "provider",
      header: _temp5,
      cell: t7 => {
        const {
          row: row_2
        } = t7;
        const provider = row_2.getValue("provider") as any;
        const displayValue_1 = provider && provider.givenName && provider.familyName ? `${provider.givenName} ${provider.familyName}` : "\u2014";
        const npiValue = provider?.npiNumber || null;
        return <CellWrapper><div className="max-w-[500px] truncate font-medium no-select"><div><CopyableContent text={displayValue_1} /></div>{npiValue && <div className="text-sm text-muted-foreground mt-1">NPI: <CopyableContent text={npiValue} /></div>}</div></CellWrapper>;
      },
      filterFn: "arrIncludesSome",
      accessorFn: _temp6
    }, {
      accessorKey: "specialty",
      header: _temp7,
      cell: t8 => {
        const {
          row: row_4
        } = t8;
        const value_2 = row_4.getValue("specialty") as string;
        return <CellWrapper><span className="no-select"><CopyableContent text={value_2 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "location",
      header: _temp8,
      cell: t9 => {
        const {
          row: row_5
        } = t9;
        const location = row_5.getValue("location") as any;
        const displayValue_2 = location && location.name ? location.name : "\u2014";
        return <CellWrapper><span className="max-w-[500px] truncate font-medium no-select"><CopyableContent text={displayValue_2} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "specialtyFolder",
      header: _temp9,
      cell: t10 => {
        const {
          row: row_6
        } = t10;
        const value_3 = row_6.getValue("specialtyFolder") as string;
        return <CellWrapper><span className="no-select"><CopyableContent text={value_3 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "surgeryName",
      header: _temp10,
      cell: t11 => {
        const {
          row: row_7
        } = t11;
        const value_4 = row_7.getValue("surgeryName") as string;
        return <CellWrapper><span className="max-w-[500px] truncate font-medium no-select"><CopyableContent text={value_4 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "hospitalAbbrev",
      header: _temp11,
      cell: t12 => {
        const {
          row: row_8
        } = t12;
        const value_5 = row_8.getValue("hospitalAbbrev") as string;
        return <CellWrapper><span className="no-select"><CopyableContent text={value_5 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "orderNumber",
      header: _temp12,
      cell: t13 => {
        const {
          row: row_9
        } = t13;
        const value_6 = row_9.getValue("orderNumber") as string;
        return <CellWrapper><span className="no-select"><CopyableContent text={value_6 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "signatureRequired",
      header: _temp13,
      cell: t14 => {
        const {
          row: row_10
        } = t14;
        const value_7 = row_10.getValue("signatureRequired") as boolean;
        const displayValue_3 = value_7 !== null && value_7 !== undefined ? value_7 ? "Yes" : "No" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_3} /></span></CellWrapper>;
      },
      filterFn: _temp14
    }, {
      accessorKey: "flowSheetType",
      header: _temp15,
      cell: t15 => {
        const {
          row: row_12
        } = t15;
        const value_9 = row_12.getValue("flowSheetType") as string;
        return <CellWrapper><span className="no-select"><CopyableContent text={value_9 || "\u2014"} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateOfEvent",
      header: _temp16,
      cell: t16 => {
        const {
          row: row_13
        } = t16;
        const value_10 = row_13.getValue("dateOfEvent") as string;
        const displayValue_4 = value_10 ? formatDate(value_10) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_4} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateOfDocument",
      header: _temp17,
      cell: t17 => {
        const {
          row: row_14
        } = t17;
        const value_11 = row_14.getValue("dateOfDocument") as string;
        const displayValue_5 = value_11 ? formatDate(value_11) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_5} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateReceived",
      header: _temp18,
      cell: t18 => {
        const {
          row: row_15
        } = t18;
        const value_12 = row_15.getValue("dateReceived") as string;
        const displayValue_6 = value_12 ? formatDate(value_12) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_6} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "dateSigned",
      header: _temp19,
      cell: t19 => {
        const {
          row: row_16
        } = t19;
        const value_13 = row_16.getValue("dateSigned") as string;
        const displayValue_7 = value_13 ? formatDate(value_13) || "\u2014" : "\u2014";
        return <CellWrapper><span className="no-select"><CopyableContent text={displayValue_7} /></span></CellWrapper>;
      }
    }, {
      accessorKey: "diagnosisList",
      header: _temp20,
      cell: t20 => {
        const {
          row: row_17
        } = t20;
        const diagnoses = row_17.getValue("diagnosisList") as any[] | undefined;
        if (!diagnoses || diagnoses.length === 0) {
          return <CellWrapper><span className="no-select">—</span></CellWrapper>;
        }
        return <CellWrapper><div className="max-w-[500px] no-select"><ul className="list-disc list-inside space-y-1">{diagnoses.map(_temp21)}</ul></div></CellWrapper>;
      },
      filterFn: "arrIncludesSome"
    }];
    t4 = allColumns.filter(column_16 => {
      const fieldName = "accessorKey" in column_16 && typeof column_16.accessorKey === "string" ? column_16.accessorKey : "";
      if (fieldName === "classification") {
        return true;
      }
      return isFieldEnabled(enabledFields, fieldName) && dataWithClassification.some(item => hasValue(item[fieldName as keyof ProgressNoteWithClassification]));
    });
    $[4] = dataWithClassification;
    $[5] = enabledFields;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const columns = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-md font-semibold flex items-center gap-2"><FileText className="h-6 w-6" />Progress Notes</CardTitle></CardHeader>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== columns || $[9] !== dataWithClassification || $[10] !== mappedColumnFilters) {
    t6 = <Card className="w-full">{t5}<CardContent><div className="rounded-lg overflow-hidden border"><DataTable columns={columns} data={dataWithClassification} columnFilters={mappedColumnFilters} ToolbarComponent={ProgressNoteToolbar} getRowUrl={_temp22} /></div></CardContent></Card>;
    $[8] = columns;
    $[9] = dataWithClassification;
    $[10] = mappedColumnFilters;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function _temp22() {
  return "";
}
function _temp21(diagnosis, index) {
  return <li key={index} className="text-sm"><CopyableContent text={`${formatICD10CodeForDisplay(diagnosis.diagnosisCode)} - ${diagnosis.diagnosisText}`} /></li>;
}
function _temp20(t0) {
  const {
    column: column_15
  } = t0;
  return <DataTableColumnHeader column={column_15} title="Diagnoses" />;
}
function _temp19(t0) {
  const {
    column: column_14
  } = t0;
  return <DataTableColumnHeader column={column_14} title="Signed Date" />;
}
function _temp18(t0) {
  const {
    column: column_13
  } = t0;
  return <DataTableColumnHeader column={column_13} title="Received Date" />;
}
function _temp17(t0) {
  const {
    column: column_12
  } = t0;
  return <DataTableColumnHeader column={column_12} title="Document Date" />;
}
function _temp16(t0) {
  const {
    column: column_11
  } = t0;
  return <DataTableColumnHeader column={column_11} title="Event Date" />;
}
function _temp15(t0) {
  const {
    column: column_10
  } = t0;
  return <DataTableColumnHeader column={column_10} title="Flow Sheet" />;
}
function _temp14(row_11, id, value_8) {
  const rowValue = row_11.getValue(id) as boolean;
  return value_8.includes(rowValue ? "true" : "false");
}
function _temp13(t0) {
  const {
    column: column_9
  } = t0;
  return <DataTableColumnHeader column={column_9} title="Signature" />;
}
function _temp12(t0) {
  const {
    column: column_8
  } = t0;
  return <DataTableColumnHeader column={column_8} title="Order Number" />;
}
function _temp11(t0) {
  const {
    column: column_7
  } = t0;
  return <DataTableColumnHeader column={column_7} title="Hospital" />;
}
function _temp10(t0) {
  const {
    column: column_6
  } = t0;
  return <DataTableColumnHeader column={column_6} title="Surgery" />;
}
function _temp9(t0) {
  const {
    column: column_5
  } = t0;
  return <DataTableColumnHeader column={column_5} title="Specialty Folder" />;
}
function _temp8(t0) {
  const {
    column: column_4
  } = t0;
  return <DataTableColumnHeader column={column_4} title="Location" />;
}
function _temp7(t0) {
  const {
    column: column_3
  } = t0;
  return <DataTableColumnHeader column={column_3} title="Specialty" />;
}
function _temp6(row_3) {
  const provider_0 = row_3.provider;
  return provider_0;
}
function _temp5(t0) {
  const {
    column: column_2
  } = t0;
  return <DataTableColumnHeader column={column_2} title="Provider" />;
}
function _temp4(t0) {
  const {
    column: column_1
  } = t0;
  return <DataTableColumnHeader column={column_1} title="Type" />;
}
function _temp3(t0) {
  const {
    column: column_0
  } = t0;
  return <DataTableColumnHeader column={column_0} title="Note Date" />;
}
function _temp2(t0) {
  const {
    column
  } = t0;
  return <DataTableColumnHeader column={column} title="Classification" />;
}
function _temp(t0) {
  const {
    children
  } = t0;
  return <div className="w-full p-2">{children}</div>;
}